import { createRouter, createWebHashHistory } from 'vue-router';

import Dashboard from '../Dashboard.vue';
import Analytics from '../Analytics.vue';
import ProcurementsList from '../ProcurementsList.vue';
import { useCountriesStore } from '../stores/countries';
import { useCitiesStore } from '../stores/cities';
import { useCityPopulationRangesStore } from '../stores/cityPopulationRanges';
import { useProcurementTypesStore } from '../stores/procurementTypes';
import { useBusinessModelTypologiesStore } from '../stores/businessModelTypologies';
import { useRouteLoadingStore } from '../stores/routeLoading';

const { caseStudiesArchiveUrl, caseStudyUrls, procurementUrls, comparisonPageUrl, glossaryPageUrl, referencesPageUrl, landingPageUrl } = window.marketToolData ?? {};

function withLang(url) {
  const match = window.location.pathname.match(/^\/([a-z]{2})\//);
  if (!match) return url;
  return url.replace(/^(https?:\/\/[^/]+)\//, `$1/${match[1]}/`);
}

// vue-router 4 silently drops any route record that has none of
// component/components/children/redirect from its matcher, which means a
// beforeEnter guard on such a route would just never fire. These routes
// exist only to hard-navigate away via window.location.href before anything
// renders, so this null component is never actually displayed — it's here
// purely to keep the route registered. (redirect would also "fix" this, but
// redirect is resolved before beforeEnter runs, so it would skip the guard
// entirely instead of exposing it.)
const NoopRoute = { render: () => null };

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      component: Dashboard,
    },
    {
      path: '/procurements',
      component: ProcurementsList,
    },
    {
      path: '/procurements/:id',
      component: NoopRoute,
      beforeEnter: (to) => {
        const id = to.params.id;
        if (id.includes(',')) {
          window.location.href = withLang(comparisonPageUrl) + '?ids=' + id;
        } else {
          window.location.href = withLang(procurementUrls?.[id] ?? comparisonPageUrl);
        }
      },
    },
    {
      path: '/analytics',
      component: Analytics,
    },
    {
      path: '/glossary',
      component: NoopRoute,
      beforeEnter: () => { window.location.href = withLang(glossaryPageUrl); },
    },
    {
      path: '/references',
      component: NoopRoute,
      beforeEnter: () => { window.location.href = withLang(referencesPageUrl); },
    },
    {
      path: '/case-studies',
      component: NoopRoute,
      beforeEnter: () => { window.location.href = withLang(caseStudiesArchiveUrl); },
    },
    {
      path: '/case-studies/:citySlug',
      component: NoopRoute,
      beforeEnter: (to) => { window.location.href = withLang(caseStudyUrls[to.params.citySlug] ?? caseStudiesArchiveUrl); },
    },
  ],
});

// The router resolves as soon as the target component is created, well
// before its onMounted data fetch finishes, so the loader has to be started
// here and stopped by each route component itself once its own critical
// data (translations + whatever the page needs to render meaningfully) is
// ready. useXStore() has to be called inside the guard rather than at module
// scope, since this file is imported before Pinia is installed on the app
// (see main.js).
router.beforeEach((to, from) => {
  if (to.path !== from.path) {
    useRouteLoadingStore().start();
  }
});

// Filter selections are held in shared Pinia stores rather than per-page
// state, so without this they'd silently carry over between pages (e.g. a
// city picked on the dashboard would still be narrowing the procurements
// list). useXStore() has to be called here, inside the guard, rather than at
// module scope, since this file is imported before Pinia is installed on
// the app (see main.js).
router.afterEach((to, from) => {
  if (to.path === from.path) {
    return;
  }

  useCountriesStore().resetActiveCountries();
  useCitiesStore().resetActiveCities();
  useCityPopulationRangesStore().resetActiveRanges();
  useProcurementTypesStore().resetActiveProcurementTypes();
  useBusinessModelTypologiesStore().resetActiveBusinessModelTypologies();
});

export default router;
